import React, { useState } from 'react';
import {
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Typography,
  Box,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { IClient } from '../../types/my-types';
import { standardTableStyles } from '../common/tableStyles';
import ClientDialog from './ClientDialog';
import ClientDeleteDialog from './ClientDeleteDialog';

interface OrgClientsProps {
  clients: IClient[];
  isLoading: boolean;
  isError: boolean;
  orgId: string;
}

function OrgClients({ clients, isLoading, isError, orgId }: OrgClientsProps) {
  const [editClient, setEditClient] = useState<IClient | null>(null);
  const [deleteClient, setDeleteClient] = useState<IClient | null>(null);

  if (isError) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', color: '#5f6d7e' }}>
        Failed to load clients.
      </Box>
    );
  }

  return (
    <div data-cy="org-clients-table" style={{ width: '100%', overflowX: 'auto' }}>
      <Table sx={standardTableStyles.tableSx}>
        <TableHead sx={standardTableStyles.tableHeadSx}>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Abbreviation</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Data Pools</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody sx={standardTableStyles.tableBodySx}>
          {isLoading
            ? Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton animation="wave" variant="rounded" height={14} width={`${50 + (i * 23) % 40}%`} sx={{ borderRadius: '4px' }} /></TableCell>
                  <TableCell><Skeleton animation="wave" variant="rounded" height={14} width={60} sx={{ borderRadius: '4px' }} /></TableCell>
                  <TableCell><Skeleton animation="wave" variant="rounded" height={14} width={120} sx={{ borderRadius: '4px' }} /></TableCell>
                  <TableCell><Skeleton animation="wave" variant="rounded" height={14} width={100} sx={{ borderRadius: '4px' }} /></TableCell>
                  <TableCell align="right"><Skeleton animation="wave" variant="rounded" height={14} width={48} sx={{ borderRadius: '4px', ml: 'auto' }} /></TableCell>
                </TableRow>
              ))
            : null}
          {!isLoading && clients.map((client) => (
            <TableRow key={client.id} data-cy={`client-row-${client.abbr}`}>
              <TableCell data-cy={`client-name-${client.abbr}`}>
                {client.name}
              </TableCell>
              <TableCell data-cy={`client-abbr-${client.abbr}`}>
                <Typography
                  component="span"
                  sx={{ fontFamily: 'monospace', fontSize: '13px', color: '#1a2332' }}
                >
                  {client.abbr}
                </Typography>
              </TableCell>
              <TableCell data-cy={`client-desc-${client.abbr}`} sx={{ color: '#5f6d7e' }}>
                {client.description || <span>&mdash;</span>}
              </TableCell>
              <TableCell data-cy={`client-pools-${client.abbr}`} sx={{ color: '#5f6d7e' }}>
                {client.data_pools.length > 0
                  ? client.data_pools.join(', ')
                  : <span>&mdash;</span>}
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Edit">
                  <IconButton
                    size="small"
                    onClick={() => setEditClient(client)}
                    data-cy={`btn-edit-client-${client.abbr}`}
                  >
                    <EditOutlinedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    onClick={() => setDeleteClient(client)}
                    data-cy={`btn-delete-client-${client.abbr}`}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
          {!isLoading && clients.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ py: 4, color: '#5f6d7e' }}>
                No clients found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {editClient && (
        <ClientDialog
          open
          mode="edit"
          client={editClient}
          orgId={orgId}
          existingClients={clients}
          onClose={() => setEditClient(null)}
        />
      )}

      {deleteClient && (
        <ClientDeleteDialog
          open
          client={deleteClient}
          onClose={() => setDeleteClient(null)}
        />
      )}
    </div>
  );
}

export default OrgClients;
